import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { HelpCategory } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { InternalBadge } from './internal-badge';

/** Column model for the help categories table: search over name and handle, client sorts. */
export function useHelpCategoryColumns() {
  const { t } = useTranslation('helpCenter');
  return useMemo<Array<DataTableColumnDef<HelpCategory>>>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.name} ${row.handle} ${row.description ?? ''}`,
        header: t('categories.columns.category'),
        meta: { sortField: 'name', lockVisibility: true },
        cell: ({ row }) => (
          <div className="min-w-0">
            <span className="block truncate font-medium">{row.original.name}</span>
            <code className="text-muted-foreground block truncate text-xs">
              {row.original.path}
            </code>
          </div>
        ),
      },
      {
        id: 'visibility',
        accessorKey: 'visibility',
        header: t('categories.columns.visibility'),
        meta: { searchable: false },
        cell: ({ row }) =>
          row.original.visibility === 'internal' ? (
            <InternalBadge />
          ) : (
            <Badge variant="outline" className="text-muted-foreground font-normal">
              {t('visibility.public')}
            </Badge>
          ),
      },
      {
        id: 'articleCount',
        accessorKey: 'articleCount',
        header: t('categories.columns.articles'),
        meta: { searchable: false },
        cell: ({ row }) => (
          <span className="tabular-nums">
            {t('categories.articleCount', {
              live: row.original.liveCount,
              count: row.original.articleCount,
            })}
          </span>
        ),
      },
      {
        id: 'position',
        accessorKey: 'position',
        header: t('categories.columns.position'),
        meta: { sortField: 'position', searchable: false },
        cell: ({ row }) => <span className="tabular-nums">{row.original.position}</span>,
      },
    ],
    [t],
  );
}
